import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import {
  SessionNotStartedError,
  SessionNotFoundError,
  SessionExpiredError,
  SessionClosedError,
  UserNotFoundError,
  UserNotAuthorizedError,
  PinNotFoundError,
  PartyHasAlreadyJoinedError,
} from '@communication'
import i18next from 'i18next'
import {useTranslation} from 'i18next-vue'
import usePreCallSpeed from '@/composables/usePreCallSpeed'
import {JoinAbortedError, usePreCallAction} from '@/composables/usePreCallAction'

export type MediaDevice = 'camera' | 'microphone'
export const usePreCall = defineStore('preCall', () => {
  const {t} = useTranslation()
  const {validatingSpeed, slowNetwork} = usePreCallSpeed()
  const {
    sessionValidationError,
    pin,
    joinMode,
    joinError,
    validatingSession,
    isJoining,
    validInput,
    validateSession,
    joinSession,
  } = usePreCallAction()
  const openedPanel = ref(null) // 'camera' | 'microphone' | 'speaker' | 'settings' (all of them) | null
  const permissionsModalDevice = ref<MediaDevice | null>(null)
  // The session cannot be joined (not found, expired, ...): Join stays disabled until the input changes
  const validationErrorMsg = computed(() => {
    if (sessionValidationError.value) {
      const error = sessionValidationError.value
      if (error instanceof SessionNotStartedError) {
        const sessionStart = new Date(error.response.sessionStart).toLocaleString(i18next.language)
        return t(`preCall.join.${error.code}`).replace('{value}', sessionStart)
      } else if (
        error instanceof SessionNotFoundError ||
        error instanceof SessionExpiredError ||
        error instanceof SessionClosedError ||
        error instanceof UserNotFoundError ||
        error instanceof UserNotAuthorizedError ||
        error instanceof PinNotFoundError
      ) {
        return t(`preCall.join.${error.code}`)
      }
    }
  })
  // The join itself failed (after its retries): said, and Join stays enabled to try again. Before, "already in session"
  // disabled Join for good and every other join error was silent (review M50)
  const joinErrorMsg = computed(() => {
    const error = joinError.value as any
    if (!error || error instanceof JoinAbortedError) return undefined
    if (error instanceof PartyHasAlreadyJoinedError) {
      // Still held by a live connection after all attempts: most likely another tab, window or device of this party
      return t('preCall.announcement.alreadyJoined', {defaultValue: t(`preCall.join.${error.code}`)})
    }
    if (error instanceof SessionExpiredError) return t(`preCall.join.${error.code}`)
    if (error?.message === 'SESSION_NOT_FOUND') return t('preCall.join.sessionNotFound')
    return t('preCall.join.connectionFailed', {
      defaultValue: 'Could not connect to the session. Check your internet connection and try again.',
    })
  })
  const sessionErrorMsg = computed(() => validationErrorMsg.value || joinErrorMsg.value)
  const isJoinButtonDisabled = computed(
    () => !validInput.value || !!validationErrorMsg.value || validatingSession.value || validatingSpeed.value
  )
  return {
    pin,
    joinMode,
    validInput,
    isJoining,
    openedPanel,
    permissionsModalDevice,
    validateSession,
    sessionErrorMsg,
    joinSession,
    isJoinButtonDisabled,
    slowNetwork,
  }
})
